import type { AbilityDef, ItemDef, RelicDef } from '@gra/content';
import iconFiles from './icon-files.json';

/** Ikony namalowane przez AI (npm run asset:icons) — mają pierwszeństwo przed rysowanymi kodem. */
const AI_ICONS = new Set<string>(iconFiles as string[]);
const aiIcon = (key: string): string | null => (AI_ICONS.has(key) ? `/icons/${key.replace(':', '-')}.webp` : null);

const cache = new Map<string, string>();
const S = 128;

function mix(hex: string, other: string, k: number): string {
  const a = parseInt(hex.slice(1), 16);
  const b = parseInt(other.slice(1), 16);
  const ch = (sh: number) => Math.round(((a >> sh) & 255) * (1 - k) + ((b >> sh) & 255) * k);
  return `rgb(${ch(16)},${ch(8)},${ch(0)})`;
}

/** Wypełnienie symbolu: biały żar w środku przechodzący w kolor czaru. */
function glyphFill(ctx: CanvasRenderingContext2D, color: string): CanvasGradient {
  const g = ctx.createRadialGradient(S * 0.45, S * 0.4, 4, S / 2, S / 2, S * 0.55);
  // Jasny, ale nie biały rdzeń: malowane ikony mają nasycone kolory, nie prześwietlone plamy.
  g.addColorStop(0, mix(color, '#fff6e0', 0.55));
  g.addColorStop(0.4, color);
  g.addColorStop(1, mix(color, '#000000', 0.35));
  return g;
}

/**
 * Ikona umiejętności malowana kodem: głębokie tło z poświatą, świecący symbol
 * z białym rdzeniem, połysk i fazowana metalowa ramka. Symbol zależy od ability.fx.
 */
export function abilityIcon(def: AbilityDef): string {
  const ai = aiIcon(`ability:${def.id}`);
  if (ai) return ai;
  return paintIcon(`ability:${def.id}`, def.color, (ctx) => drawGlyph(ctx, def.fx, def.color));
}

const STAT_COLORS: Record<string, string> = {
  maxHealth: '#e0484a', moveSpeed: '#5fd38a', pickupRadius: '#7ee8ff', critChance: '#ffcc4d', armor: '#9aa8b8',
  regen: '#6fdc6f', damage: '#ff7a45', cooldown: '#8fb4ff', projectileCount: '#c58cff', projectileSpeed: '#b8e0ff',
  pierce: '#e6dcb8', area: '#ffb36b', range: '#9fe0ff', goldGain: '#ffd04a', xpGain: '#b07bff', chestRate: '#e8a040',
  relicFind: '#e8a040', critDamage: '#ffcc4d', executeBelow: '#c04040', lifeOnKill: '#e0484a', dodgeChance: '#5fd38a',
  barrier: '#7ee8ff', thorns: '#9aa8b8', luck: '#ffd04a',
};

/** Efekty bez własnego symbolu dostają symbol najbliższej statystyki. */
const STAT_GLYPH_ALIAS: Record<string, string> = {
  relicFind: 'chestRate', critDamage: 'critChance', executeBelow: 'damage', lifeOnKill: 'regen', dodgeChance: 'moveSpeed',
  barrier: 'armor', thorns: 'armor', luck: 'goldGain',
};

/** Ikona talentu statystycznego: ten sam styl co ikony umiejętności, symbol wg statystyki. */
export function statIcon(stat: string): string {
  const ai = aiIcon(`stat:${stat}`);
  if (ai) return ai;
  const alias = STAT_GLYPH_ALIAS[stat];
  if (alias && aiIcon(`stat:${alias}`)) return aiIcon(`stat:${alias}`)!;
  const color = STAT_COLORS[stat] ?? '#f0cf7a';
  return paintIcon(`stat:${stat}`, color, (ctx) => drawStatGlyph(ctx, alias ?? stat));
}

/** Ikona talentu z drzewka według pola `icon` z danych ("stat:x" / "upgrade:y"), inaczej jak upgradeIcon. */
export function treeIcon(id: string, stat: string, icon?: string): string {
  if (icon?.startsWith('upgrade:')) return aiIcon(icon) ?? upgradeIcon(id, stat);
  if (icon?.startsWith('stat:')) return statIcon(icon.slice(5));
  return upgradeIcon(id, stat);
}

/**
 * Ikona przedmiotu wyprawy malowana kodem: pierścień (obrączka z kamieniem), amulet (wisior na łańcuszku)
 * albo talizman (runiczny kamień), w kolorze kamienia, na tle w kolorze rzadkości.
 */
export function relicIcon(def: RelicDef, rarityColor: string): string {
  return paintIcon(`relic:${def.id}`, rarityColor, (ctx) => drawRelicGlyph(ctx, def.kind, def.color));
}

function drawRelicGlyph(ctx: CanvasRenderingContext2D, kind: RelicDef['kind'], gem: string): void {
  const m = S / 2;
  const gemFill = () => {
    const g = ctx.createRadialGradient(m - 6, m - 6, 2, m, m, 22);
    g.addColorStop(0, '#ffffff');
    g.addColorStop(0.4, mix(gem, '#ffffff', 0.35));
    g.addColorStop(1, gem);
    return g;
  };
  ctx.lineWidth = 9;
  if (kind === 'pierscien') {
    ctx.beginPath();
    ctx.arc(m, m + 12, 30, 0, Math.PI * 2);
    ctx.stroke();
    ctx.save();
    ctx.fillStyle = gemFill();
    ctx.beginPath();
    ctx.moveTo(m, m - 40);
    ctx.lineTo(m + 16, m - 22);
    ctx.lineTo(m, m - 6);
    ctx.lineTo(m - 16, m - 22);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  } else if (kind === 'amulet') {
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.moveTo(m - 34, m - 44);
    ctx.quadraticCurveTo(m, m + 4, m + 34, m - 44);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(m, m + 14, 26, 0, Math.PI * 2);
    ctx.fill();
    ctx.save();
    ctx.fillStyle = gemFill();
    ctx.beginPath();
    ctx.arc(m, m + 14, 15, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  } else {
    ctx.beginPath();
    ctx.moveTo(m, m - 46);
    ctx.lineTo(m + 30, m - 10);
    ctx.lineTo(m + 18, m + 42);
    ctx.lineTo(m - 18, m + 42);
    ctx.lineTo(m - 30, m - 10);
    ctx.closePath();
    ctx.fill();
    ctx.save();
    ctx.strokeStyle = gemFill();
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.moveTo(m, m - 26);
    ctx.lineTo(m, m + 28);
    ctx.moveTo(m - 14, m - 6);
    ctx.lineTo(m + 14, m + 8);
    ctx.moveTo(m + 14, m - 6);
    ctx.lineTo(m - 14, m + 8);
    ctx.stroke();
    ctx.restore();
  }
}

/**
 * Ikona zbroi malowana kodem (tułów, nogi, dłonie, stopy) w kolorze i materiale zbroi, na tle w kolorze rzadkości.
 * Do czasu modeli zbroi z AI to jedyny obraz zbroi poza kolorem części ciała bohatera.
 */
export function armorIcon(def: ItemDef, rarityColor: string): string {
  const look = def.look!;
  return paintIcon(`armor:${def.id}:${rarityColor}`, rarityColor, (ctx) => drawArmorGlyph(ctx, def.slot, look));
}

const ARMOR_SHAPES: Record<string, string> = {
  // Napierśnik z naramiennikami.
  chest: 'M28 34 Q40 24 52 28 L64 34 L76 28 Q88 24 100 34 L106 56 L94 60 L92 104 Q64 114 36 104 L34 60 L22 56 Z',
  // Spodnie / nagolenniki.
  legs: 'M38 22 L90 22 L94 52 L86 110 L70 110 L64 58 L58 110 L42 110 L34 52 Z',
  // Rękawica z mankietem.
  hands: 'M44 108 L44 80 Q36 70 36 54 L36 34 Q36 28 42 28 Q48 28 48 34 L48 50 L50 26 Q50 20 56 20 Q62 20 62 26 L62 48 L64 22 Q64 16 70 16 Q76 16 76 22 L76 48 L78 28 Q78 22 84 22 Q90 22 90 28 L90 66 Q90 80 82 86 L82 108 Z',
  // But z cholewą.
  feet: 'M46 18 L78 18 L78 74 Q96 78 108 90 Q112 100 104 106 L34 106 Q28 106 30 96 L34 74 Q40 70 44 62 Z',
};

function drawArmorGlyph(ctx: CanvasRenderingContext2D, slot: string, look: NonNullable<ItemDef['look']>): void {
  const path = new Path2D(ARMOR_SHAPES[slot] ?? ARMOR_SHAPES.chest);
  const base = look.color;
  // Metal błyszczy (jasny refleks), tkanina i futro są matowe, skóra pośrodku.
  const shine = look.material === 'metal' ? 0.55 : look.material === 'leather' ? 0.25 : 0.12;
  const g = ctx.createLinearGradient(20, 16, 108, 112);
  g.addColorStop(0, mix(base, '#ffffff', shine));
  g.addColorStop(0.45, base);
  g.addColorStop(1, mix(base, '#000000', 0.55));
  ctx.fillStyle = g;
  ctx.fill(path);
  ctx.shadowBlur = 0;
  // Faktura: futro — krótkie kłaczki, tkanina — szwy, metal — nity.
  ctx.save();
  ctx.clip(path);
  if (look.material === 'fur') {
    ctx.strokeStyle = 'rgba(0,0,0,0.28)';
    ctx.lineWidth = 2;
    for (let i = 0; i < 70; i++) {
      const x = 20 + ((i * 37) % 90);
      const y = 18 + ((i * 53) % 92);
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + 3, y + 7);
      ctx.stroke();
    }
  } else if (look.material === 'cloth') {
    ctx.strokeStyle = 'rgba(255,240,210,0.18)';
    ctx.setLineDash([3, 4]);
    ctx.lineWidth = 2;
    for (let y = 30; y < 112; y += 16) {
      ctx.beginPath();
      ctx.moveTo(16, y);
      ctx.lineTo(112, y + 6);
      ctx.stroke();
    }
  } else if (look.material === 'metal') {
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    for (const [x, y] of [[42, 46], [86, 46], [44, 92], [84, 92], [64, 40]]) {
      ctx.beginPath();
      ctx.arc(x!, y!, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.restore();
  // Obrys i wykończenie (np. złote lamówki).
  ctx.lineWidth = look.trim ? 5 : 3;
  ctx.strokeStyle = look.trim ?? mix(base, '#000000', 0.65);
  ctx.stroke(path);
}

/** Malowana ikona przedmiotu (AI) albo null — wtedy ikona z modelu 3D. */
export function itemIcon(itemId: string): string | null {
  return aiIcon(`item:${itemId}`);
}

/** Malowana grafika (tło obozu, strefy, gałęzie talentów) albo null, gdy jeszcze jej nie ma. */
export function artImage(name: string): string | null {
  return AI_ICONS.has(`art:${name}`) ? `/art/${name}.webp` : null;
}

/** Ikona talentu z drzewka: własna z AI, a bez niej — ikona jego statystyki. */
export function upgradeIcon(id: string, stat: string): string {
  return aiIcon(`upgrade:${id}`) ?? statIcon(stat);
}

/** Portret rasy (AI) albo null. */
export function raceIcon(raceId: string): string | null {
  return aiIcon(`race:${raceId}`);
}

/** Ikona klasy: namalowana przez AI, a bez niej — ikona startowej umiejętności klasy. */
export function classIcon(classId: string, starting: AbilityDef): string {
  return aiIcon(`class:${classId}`) ?? abilityIcon(starting);
}

/**
 * Pociągnięcia pędzla na całej ikonie (stałe dla danego klucza): ikony rysowane kodem dostają fakturę farby
 * jak ikony malowane przez AI, zamiast gładkich gradientów.
 */
/** Szare kolory (zwykła rzadkość) dają tło w ciepłym brązie zamiast wypranej szarości. */
function tone(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return Math.max(r, g, b) - Math.min(r, g, b) < 40 ? '#8a6a3a' : hex;
}

function paintStrokes(ctx: CanvasRenderingContext2D, key: string): void {
  let seed = 0;
  for (let i = 0; i < key.length; i++) seed = (seed * 31 + key.charCodeAt(i)) | 0;
  const rnd = () => ((seed = (seed * 1664525 + 1013904223) | 0) >>> 0) / 4294967296;
  ctx.save();
  ctx.globalCompositeOperation = 'soft-light';
  ctx.lineCap = 'round';
  for (let i = 0; i < 160; i++) {
    const x = rnd() * S;
    const y = rnd() * S;
    const len = 6 + rnd() * 18;
    const a = -0.9 + rnd() * 0.5; // pędzel prowadzony ukośnie, jak na malowanych ikonach
    ctx.strokeStyle = rnd() < 0.45 ? `rgba(255,236,200,${0.05 + rnd() * 0.1})` : `rgba(0,0,0,${0.15 + rnd() * 0.22})`;
    ctx.lineWidth = 1.5 + rnd() * 3.5;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + Math.cos(a) * len, y + Math.sin(a) * len);
    ctx.stroke();
  }
  // Ciepła tonacja jak w malowanych ikonach (lekko złote światła, brązowe cienie).
  ctx.globalCompositeOperation = 'overlay';
  ctx.fillStyle = 'rgba(150, 100, 40, 0.18)';
  ctx.fillRect(0, 0, S, S);
  ctx.restore();
}

function paintIcon(key: string, color: string, glyph: (ctx: CanvasRenderingContext2D) => void): string {
  const cached = cache.get(key);
  if (cached) return cached;
  const def = { color: tone(color) };
  const c = document.createElement('canvas');
  c.width = c.height = S;
  const ctx = c.getContext('2d')!;
  const m = S / 2;

  // Tło: ciemny kamień + kolorowa poświata za symbolem.
  const bg = ctx.createRadialGradient(m, m * 0.9, 6, m, m, S * 0.75);
  bg.addColorStop(0, mix(def.color, '#000000', 0.5));
  bg.addColorStop(0.55, mix(def.color, '#0b0805', 0.84));
  bg.addColorStop(1, '#050403');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, S, S);
  // Promienie światła za symbolem.
  ctx.save();
  ctx.translate(m, m);
  ctx.globalCompositeOperation = 'lighter';
  for (let i = 0; i < 12; i++) {
    ctx.rotate((Math.PI * 2) / 12);
    const ray = ctx.createLinearGradient(0, 0, 0, -S * 0.6);
    ray.addColorStop(0, mix(def.color, '#000000', 0.72));
    ray.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = ray;
    ctx.beginPath();
    ctx.moveTo(-4, 0);
    ctx.lineTo(0, -S * 0.6);
    ctx.lineTo(4, 0);
    ctx.fill();
  }
  ctx.restore();

  // Symbol.
  ctx.save();
  ctx.shadowColor = mix(def.color, '#000000', 0.2);
  ctx.shadowBlur = 12;
  ctx.fillStyle = glyphFill(ctx, color);
  ctx.strokeStyle = glyphFill(ctx, color);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  glyph(ctx);
  ctx.restore();

  // Połysk w górnej części, jak szkło nad ikoną.
  const gloss = ctx.createLinearGradient(0, 0, 0, S * 0.5);
  gloss.addColorStop(0, 'rgba(255,240,210,0.12)');
  gloss.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gloss;
  ctx.beginPath();
  ctx.ellipse(m, 0, S * 0.75, S * 0.42, 0, 0, Math.PI * 2);
  ctx.fill();

  // Winieta i fazowana ramka: złota krawędź z ciemną linią wewnętrzną.
  const vig = ctx.createRadialGradient(m, m, S * 0.35, m, m, S * 0.72);
  vig.addColorStop(0, 'rgba(0,0,0,0)');
  vig.addColorStop(1, 'rgba(0,0,0,0.8)');
  ctx.fillStyle = vig;
  ctx.fillRect(0, 0, S, S);
  paintStrokes(ctx, key);
  // Ramka jak przy ikonach z AI: czarna krawędź, ciemnobrązowa faza, cień do środka.
  const frame = ctx.createLinearGradient(0, 0, S, S);
  frame.addColorStop(0, '#b8925a');
  frame.addColorStop(0.5, '#4a361c');
  frame.addColorStop(1, '#8a6a3a');
  ctx.lineWidth = 4;
  ctx.strokeStyle = '#050403';
  ctx.strokeRect(2, 2, S - 4, S - 4);
  ctx.lineWidth = 2;
  ctx.strokeStyle = frame;
  ctx.strokeRect(5, 5, S - 10, S - 10);
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = 'rgba(0,0,0,0.7)';
  ctx.strokeRect(7, 7, S - 14, S - 14);

  const url = c.toDataURL();
  cache.set(key, url);
  return url;
}

function drawStatGlyph(ctx: CanvasRenderingContext2D, stat: string): void {
  const m = S / 2;
  ctx.lineWidth = 7;
  switch (stat) {
    case 'maxHealth':
    case 'regen': {
      // Serce (zdrowie) albo serce z plusem (regeneracja).
      ctx.beginPath();
      ctx.moveTo(m, m + 34);
      ctx.bezierCurveTo(m - 52, m - 4, m - 26, m - 46, m, m - 18);
      ctx.bezierCurveTo(m + 26, m - 46, m + 52, m - 4, m, m + 34);
      ctx.fill();
      if (stat === 'regen') {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(m - 4, m - 18, 8, 32);
        ctx.fillRect(m - 16, m - 6, 32, 8);
      }
      break;
    }
    case 'armor': {
      ctx.beginPath();
      ctx.moveTo(m, m - 44);
      ctx.lineTo(m + 36, m - 30);
      ctx.quadraticCurveTo(m + 34, m + 22, m, m + 44);
      ctx.quadraticCurveTo(m - 34, m + 22, m - 36, m - 30);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'damage': {
      ctx.save();
      ctx.translate(m, m);
      ctx.rotate(Math.PI / 4);
      ctx.beginPath();
      ctx.moveTo(0, -50);
      ctx.lineTo(8, -40);
      ctx.lineTo(8, 16);
      ctx.lineTo(-8, 16);
      ctx.lineTo(-8, -40);
      ctx.closePath();
      ctx.fill();
      ctx.fillRect(-22, 16, 44, 8);
      ctx.fillRect(-5, 24, 10, 22);
      ctx.restore();
      break;
    }
    case 'cooldown': {
      // Klepsydra.
      ctx.beginPath();
      ctx.moveTo(m - 28, m - 42);
      ctx.lineTo(m + 28, m - 42);
      ctx.lineTo(m + 4, m);
      ctx.lineTo(m + 28, m + 42);
      ctx.lineTo(m - 28, m + 42);
      ctx.lineTo(m - 4, m);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'moveSpeed': {
      for (const dx of [-22, 0, 22]) {
        ctx.beginPath();
        ctx.moveTo(m + dx - 14, m - 30);
        ctx.lineTo(m + dx + 14, m);
        ctx.lineTo(m + dx - 14, m + 30);
        ctx.stroke();
      }
      break;
    }
    case 'critChance': {
      // Gwiazda trafienia krytycznego.
      ctx.beginPath();
      for (let i = 0; i < 16; i++) {
        const r = i % 2 ? 16 : 46;
        const a = (i / 16) * Math.PI * 2;
        ctx.lineTo(m + Math.cos(a) * r, m + Math.sin(a) * r);
      }
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'pickupRadius': {
      // Magnes.
      ctx.lineWidth = 16;
      ctx.beginPath();
      ctx.arc(m, m - 4, 26, Math.PI, 0);
      ctx.moveTo(m - 26, m - 4);
      ctx.lineTo(m - 26, m + 30);
      ctx.moveTo(m + 26, m - 4);
      ctx.lineTo(m + 26, m + 30);
      ctx.stroke();
      break;
    }
    case 'goldGain': {
      // Stos monet.
      for (const [dx, dy] of [[-14, 18], [14, 18], [0, -6]]) {
        ctx.beginPath();
        ctx.ellipse(m + dx!, m + dy!, 24, 12, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
      break;
    }
    case 'xpGain': {
      // Otwarta księga.
      ctx.lineWidth = 6;
      ctx.beginPath();
      ctx.moveTo(m, m - 22);
      ctx.quadraticCurveTo(m - 22, m - 34, m - 44, m - 22);
      ctx.lineTo(m - 44, m + 30);
      ctx.quadraticCurveTo(m - 22, m + 18, m, m + 30);
      ctx.quadraticCurveTo(m + 22, m + 18, m + 44, m + 30);
      ctx.lineTo(m + 44, m - 22);
      ctx.quadraticCurveTo(m + 22, m - 34, m, m - 22);
      ctx.lineTo(m, m + 30);
      ctx.stroke();
      break;
    }
    case 'chestRate': {
      // Skrzynia.
      ctx.lineWidth = 6;
      ctx.strokeRect(m - 38, m - 8, 76, 40);
      ctx.beginPath();
      ctx.moveTo(m - 38, m - 8);
      ctx.quadraticCurveTo(m, m - 44, m + 38, m - 8);
      ctx.stroke();
      ctx.fillRect(m - 7, m - 6, 14, 18);
      break;
    }
    case 'area': {
      for (const r of [14, 28, 42]) {
        ctx.beginPath();
        ctx.arc(m, m, r, 0, Math.PI * 2);
        ctx.stroke();
      }
      break;
    }
    default: {
      // Pociski: kilka strzałek w wachlarzu.
      for (const a of [-0.35, 0, 0.35]) {
        ctx.save();
        ctx.translate(m, m + 30);
        ctx.rotate(a);
        ctx.fillRect(-3, -50, 6, 46);
        ctx.beginPath();
        ctx.moveTo(-10, -46);
        ctx.lineTo(0, -62);
        ctx.lineTo(10, -46);
        ctx.fill();
        ctx.restore();
      }
    }
  }
}

function drawGlyph(ctx: CanvasRenderingContext2D, fx: string, color: string): void {
  const m = S / 2;
  switch (fx) {
    case 'slash': {
      // Półksiężyc ostrza z cieńszą smugą obok.
      ctx.beginPath();
      ctx.arc(m - 14, m + 18, 46, -Math.PI * 0.62, -Math.PI * 0.02);
      ctx.arc(m - 22, m + 26, 40, -Math.PI * 0.04, -Math.PI * 0.6, true);
      ctx.closePath();
      ctx.fill();
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(m - 14, m + 18, 30, -Math.PI * 0.55, -Math.PI * 0.1);
      ctx.stroke();
      break;
    }
    case 'cleave': {
      // Topór w uderzeniu i pęknięcia ziemi.
      ctx.save();
      ctx.translate(m, m - 4);
      ctx.rotate(-0.35);
      ctx.fillRect(-4, -40, 8, 70);
      ctx.beginPath();
      ctx.moveTo(4, -38);
      ctx.quadraticCurveTo(40, -36, 34, -4);
      ctx.quadraticCurveTo(22, -14, 4, -12);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(m - 30, m + 40);
      ctx.lineTo(m - 10, m + 32);
      ctx.lineTo(m + 2, m + 42);
      ctx.lineTo(m + 26, m + 30);
      ctx.stroke();
      break;
    }
    case 'spin': {
      // Trzy zakrzywione ostrza wirujące wokół środka.
      for (let i = 0; i < 3; i++) {
        ctx.save();
        ctx.translate(m, m);
        ctx.rotate((i * Math.PI * 2) / 3);
        ctx.beginPath();
        ctx.moveTo(0, -8);
        ctx.quadraticCurveTo(30, -30, 44, 4);
        ctx.quadraticCurveTo(26, -10, 6, 4);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      }
      ctx.beginPath();
      ctx.arc(m, m, 8, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 'orb': {
      // Kula energii z iskrami i orbitą.
      const g = ctx.createRadialGradient(m - 6, m - 6, 2, m, m, 30);
      g.addColorStop(0, '#ffffff');
      g.addColorStop(0.4, mix(color, '#ffffff', 0.4));
      g.addColorStop(1, color);
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(m, m, 26, 0, Math.PI * 2);
      ctx.fill();
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = mix(color, '#ffffff', 0.5);
      ctx.beginPath();
      ctx.ellipse(m, m, 44, 14, -0.5, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = '#ffffff';
      for (const [x, y, r] of [[m + 34, m - 30, 3], [m - 36, m + 24, 2.5], [m + 22, m + 36, 2]] as const) {
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    case 'bolt': {
      // Bełt z lotkami, lecący po skosie, ze smugą.
      ctx.save();
      ctx.translate(m, m);
      ctx.rotate(-Math.PI / 4);
      ctx.fillRect(-44, -3, 70, 6);
      ctx.beginPath();
      ctx.moveTo(26, -10);
      ctx.lineTo(48, 0);
      ctx.lineTo(26, 10);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(-44, -3);
      ctx.lineTo(-54, -14);
      ctx.lineTo(-34, -3);
      ctx.moveTo(-44, 3);
      ctx.lineTo(-54, 14);
      ctx.lineTo(-34, 3);
      ctx.fill();
      ctx.globalAlpha = 0.5;
      ctx.fillRect(-60, -1, 20, 2);
      ctx.restore();
      break;
    }
    case 'knife': {
      // Wachlarz trzech sztyletów.
      for (const a of [-0.5, 0, 0.5]) {
        ctx.save();
        ctx.translate(m, m + 30);
        ctx.rotate(a);
        ctx.beginPath();
        ctx.moveTo(0, -58);
        ctx.lineTo(7, -26);
        ctx.lineTo(0, -20);
        ctx.lineTo(-7, -26);
        ctx.closePath();
        ctx.fill();
        ctx.fillRect(-9, -22, 18, 4);
        ctx.fillRect(-3, -18, 6, 14);
        ctx.restore();
      }
      break;
    }
    case 'shard': {
      // Kryształy lodu wyrastające ukosem.
      const crystal = (x: number, y: number, len: number, w: number, rot: number) => {
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(rot);
        ctx.beginPath();
        ctx.moveTo(0, -len);
        ctx.lineTo(w, -len * 0.25);
        ctx.lineTo(w * 0.6, 0);
        ctx.lineTo(-w * 0.6, 0);
        ctx.lineTo(-w, -len * 0.25);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      };
      crystal(m, m + 34, 64, 14, 0.15);
      crystal(m - 16, m + 36, 40, 10, -0.5);
      crystal(m + 18, m + 36, 36, 9, 0.7);
      break;
    }
    case 'blades': {
      // Dwa ostrza na wspólnej orbicie wokół jasnego środka.
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(m, m, 34, 0, Math.PI * 2);
      ctx.stroke();
      for (const a of [-0.6, Math.PI - 0.6]) {
        ctx.save();
        ctx.translate(m + Math.cos(a) * 34, m + Math.sin(a) * 34);
        ctx.rotate(a + Math.PI / 2);
        ctx.beginPath();
        ctx.moveTo(0, -24);
        ctx.lineTo(9, 0);
        ctx.lineTo(0, 24);
        ctx.lineTo(-9, 0);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      }
      ctx.beginPath();
      ctx.arc(m, m, 9, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 'lightning': {
      // Zygzak błyskawicy przeskakujący między trzema punktami.
      ctx.lineWidth = 7;
      ctx.lineJoin = 'miter';
      ctx.beginPath();
      ctx.moveTo(m - 44, m - 36);
      ctx.lineTo(m - 8, m - 8);
      ctx.lineTo(m - 20, m + 2);
      ctx.lineTo(m + 16, m + 24);
      ctx.lineTo(m + 6, m + 30);
      ctx.lineTo(m + 44, m + 44);
      ctx.stroke();
      for (const [x, y] of [[m - 44, m - 36], [m - 14, m - 3], [m + 44, m + 44]] as const) {
        ctx.beginPath();
        ctx.arc(x, y, 7, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    case 'aura': {
      // Koncentryczne kręgi z drobinkami unoszącymi się w górę.
      ctx.lineWidth = 5;
      for (const r of [44, 30]) {
        ctx.beginPath();
        ctx.arc(m, m + 6, r, 0, Math.PI * 2);
        ctx.stroke();
      }
      for (const [x, y, r] of [[m, m - 30, 6], [m - 18, m - 10, 4.5], [m + 20, m - 16, 5], [m + 4, m + 4, 7]] as const) {
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    case 'totem': {
      // Pułapka: zębaty pierścień zatrzasku z kołkiem w środku.
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.arc(m, m + 10, 34, 0, Math.PI * 2);
      ctx.stroke();
      for (let i = 0; i < 10; i++) {
        const a = (i / 10) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(m + Math.cos(a - 0.15) * 34, m + 10 + Math.sin(a - 0.15) * 34);
        ctx.lineTo(m + Math.cos(a) * 20, m + 10 + Math.sin(a) * 20);
        ctx.lineTo(m + Math.cos(a + 0.15) * 34, m + 10 + Math.sin(a + 0.15) * 34);
        ctx.fill();
      }
      ctx.fillRect(m - 4, m - 46, 8, 52);
      ctx.beginPath();
      ctx.moveTo(m - 10, m - 40);
      ctx.lineTo(m, m - 56);
      ctx.lineTo(m + 10, m - 40);
      ctx.fill();
      break;
    }
    case 'flame': {
      // Płomień warstwowy: zewnętrzny w kolorze czaru, wewnętrzny żółto-biały.
      const flame = (scale: number) => {
        ctx.beginPath();
        ctx.moveTo(m, m - 48 * scale);
        ctx.bezierCurveTo(m + 30 * scale, m - 14 * scale, m + 34 * scale, m + 34 * scale, m, m + 42 * scale);
        ctx.bezierCurveTo(m - 34 * scale, m + 34 * scale, m - 30 * scale, m - 6 * scale, m - 10 * scale, m - 18 * scale);
        ctx.bezierCurveTo(m - 8 * scale, m - 2 * scale, m + 2 * scale, m - 4 * scale, m, m - 48 * scale);
        ctx.fill();
      };
      flame(1);
      ctx.fillStyle = '#ffe9a8';
      ctx.shadowColor = '#ffd27a';
      flame(0.55);
      ctx.fillStyle = '#ffffff';
      flame(0.25);
      break;
    }
  }
}
